import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/errorMessage";

// =====================================================
// Quick Cadastro: Fornecedor / Cliente / Prestador / Categoria
// (minimo necessario p/ criar e ja vincular ao lancamento)
// =====================================================
export function QuickFornecedorForm({ tipo, nomeInicial, onSuccess, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    tipo_pessoa: "juridica",
    tipo_cadastro: tipo === "receita" ? "cliente" : "fornecedor",
    nome: nomeInicial || "",
    documento: "",
    telefone_whatsapp_1: "",
    email: "",
    is_prestador_servicos: false,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!data.nome.trim()) { toast.error("Nome é obrigatório"); return; }
    if (!data.documento.trim()) { toast.error("CPF/CNPJ é obrigatório"); return; }
    setLoading(true);
    try {
      const payload = { ...data };
      if (!payload.email) delete payload.email;
      const r = await axios.post(`${API}/clientes-fornecedores`, payload);
      onSuccess(r.data?.id);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao cadastrar"));
    } finally {
      setLoading(false);
    }
  };

  const isFornecedor = data.tipo_cadastro === "fornecedor" || data.tipo_cadastro === "ambos";

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="quick-fornecedor-form">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Tipo de Pessoa</Label>
          <NativeSelect
            value={data.tipo_pessoa}
            onValueChange={(v) => setData({...data, tipo_pessoa: v})}
            options={[{ value: "juridica", label: "Jurídica (CNPJ)" }, { value: "fisica", label: "Física (CPF)" }]}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Cadastrar como</Label>
          <NativeSelect
            value={data.tipo_cadastro}
            onValueChange={(v) => setData({...data, tipo_cadastro: v})}
            options={[
              { value: "cliente", label: "Cliente" },
              { value: "fornecedor", label: "Fornecedor" },
              { value: "ambos", label: "Cliente e Fornecedor" },
            ]}
          />
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-xs">{data.tipo_pessoa === "juridica" ? "Razão Social / Nome *" : "Nome Completo *"}</Label>
        <Input
          value={data.nome}
          onChange={(e) => setData({...data, nome: e.target.value})}
          placeholder={data.tipo_pessoa === "juridica" ? "Ex: CEMIG Distribuição S.A." : "Ex: João da Silva"}
          required
          autoFocus
          data-testid="quick-forn-nome"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">{data.tipo_pessoa === "juridica" ? "CNPJ *" : "CPF *"}</Label>
          <Input
            value={data.documento}
            onChange={(e) => setData({...data, documento: e.target.value})}
            placeholder={data.tipo_pessoa === "juridica" ? "00.000.000/0000-00" : "000.000.000-00"}
            required
            data-testid="quick-forn-doc"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Telefone / WhatsApp</Label>
          <Input
            value={data.telefone_whatsapp_1}
            onChange={(e) => setData({...data, telefone_whatsapp_1: e.target.value})}
            placeholder="(11) 99999-9999"
          />
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-xs">E-mail</Label>
        <Input
          type="email"
          value={data.email}
          onChange={(e) => setData({...data, email: e.target.value})}
          placeholder="contato@exemplo.com"
        />
      </div>

      {isFornecedor && (
        <label className="flex items-center gap-2 text-xs text-slate-700 pt-1">
          <input
            type="checkbox"
            checked={data.is_prestador_servicos}
            onChange={(e) => setData({...data, is_prestador_servicos: e.target.checked})}
          />
          É <b>Prestador de Serviços</b>
        </label>
      )}

      <p className="text-[11px] text-slate-500 leading-tight">
        Cadastro mínimo. Você pode completar os dados depois em
        <b> Cadastros &gt; Clientes/Fornecedores</b>.
      </p>

      <div className="flex gap-2 pt-2">
        <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>Cancelar</Button>
        <Button
          type="submit"
          className="flex-1 bg-luvcorp-navy hover:bg-luvcorp-navy-dark text-white"
          disabled={loading}
          data-testid="quick-forn-submit"
        >
          {loading ? "Salvando..." : "Cadastrar"}
        </Button>
      </div>
    </form>
  );
}

// =====================================================
// Quick Cadastro: Categoria
// =====================================================
export function QuickCategoriaForm({ tipo, nomeInicial, onSuccess, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    nome: nomeInicial || "",
    tipo: tipo === "receita" ? "receita" : "despesa",
    cor: "#475569",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!data.nome.trim()) { toast.error("Nome é obrigatório"); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/categorias`, data);
      onSuccess(r.data);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao criar categoria"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="quick-categoria-form">
      <div className="space-y-1">
        <Label className="text-xs">Nome *</Label>
        <Input
          value={data.nome}
          onChange={(e) => setData({...data, nome: e.target.value})}
          placeholder="Ex: Aluguel, Internet, Honorários..."
          required
          autoFocus
          data-testid="quick-cat-nome"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Tipo</Label>
          <NativeSelect
            value={data.tipo}
            onValueChange={(v) => setData({...data, tipo: v})}
            options={[
              { value: "despesa", label: "Despesa" },
              { value: "receita", label: "Receita" },
              { value: "ambos", label: "Ambos" },
            ]}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Cor</Label>
          <Input
            type="color"
            value={data.cor}
            onChange={(e) => setData({...data, cor: e.target.value})}
            className="h-10 p-1"
          />
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>Cancelar</Button>
        <Button
          type="submit"
          className="flex-1 bg-luvcorp-navy hover:bg-luvcorp-navy-dark text-white"
          disabled={loading}
          data-testid="quick-cat-submit"
        >
          {loading ? "Salvando..." : "Criar Categoria"}
        </Button>
      </div>
    </form>
  );
}

